import React, { useState, useEffect, useCallback } from 'react';
import api, { getQueues } from '../services/api';
import { FiPrinter, FiDownload, FiFileText } from 'react-icons/fi';
import MultiSelectQueues from '../components/MultiSelectQueues';
import { crmGetDashboard } from '../crm/crmApi';
import '../crm/crm.css';

const QUICK_FILTERS = [
  { key: 'today', label: 'اليوم' },
  { key: 'yesterday', label: 'أمس' },
  { key: '7days', label: 'آخر 7 أيام' },
  { key: 'month', label: 'هذا الشهر' },
  { key: 'all', label: 'الكل' }
];

const computeRange = (key) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (key) {
    case 'today': return { from: today.toISOString(), to: new Date(today.getTime() + 86400000).toISOString() };
    case 'yesterday': { const y = new Date(today.getTime() - 86400000); return { from: y.toISOString(), to: today.toISOString() }; }
    case '7days': return { from: new Date(today.getTime() - 7 * 86400000).toISOString(), to: new Date(today.getTime() + 86400000).toISOString() };
    case 'month': return { from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(), to: new Date(today.getTime() + 86400000).toISOString() };
    default: return { from: '', to: '' };
  }
};

const TABS = [
  { key: 'overall', label: 'عام' },
  { key: 'queues', label: 'الكيوهات' },
  { key: 'agents', label: 'الموظفين' },
  { key: 'followups', label: 'المتابعات' },
  // Batch 3: how the calls the system captured were judged by humans.
  { key: 'classification', label: 'تصنيف التواصل' }
];

const exportCSV = (rows, filename) => {
  if (!rows || rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const csv = [headers.join(','), ...rows.map(r => headers.map(h => `"${(r[h] ?? '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const printSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const w = window.open('', '_blank');
  w.document.write(`<html dir="rtl"><head><title>تقرير</title><style>body{font-family:Tajawal,Arial,sans-serif;padding:20px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ddd;padding:8px;text-align:right}th{background:#f0f0f0}</style></head><body>${el.innerHTML}</body></html>`);
  w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
};

const ReportsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overall');
  const [activeFilter, setActiveFilter] = useState('month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [queue, setQueue] = useState([]);
  const [queues, setQueues] = useState([]);
  // Batch 3. The CRM classification counts live on the CRM dashboard endpoint,
  // NOT on /calls/reports/full — this page reports on calls, and classification
  // is a property of the opportunity a call produced. Fetched separately and
  // held separately so a CRM outage cannot blank the call reports.
  const [crm, setCrm] = useState(null);
  const [crmError, setCrmError] = useState('');

  useEffect(() => { getQueues().then(r => setQueues(r.data || [])).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let from = '', to = '';
      if (customFrom || customTo) {
        if (customFrom) from = new Date(customFrom).toISOString();
        if (customTo) { const d = new Date(customTo); d.setHours(23,59,59,999); to = d.toISOString(); }
      } else {
        const r = computeRange(activeFilter); from = r.from; to = r.to;
      }
      const params = new URLSearchParams();
      if (from) params.set('dateFrom', from);
      if (to) params.set('dateTo', to);
      if (queue && queue.length > 0) params.set("queue", queue.join(","));
      const res = await api.get(`/calls/reports/full?${params.toString()}`);
      setData(res.data);

      // Same date window, so both tabs describe the same period. Admin-only on
      // the server; a non-admin simply gets no classification tab content.
      const crmParams = {};
      if (from) crmParams.dateFrom = from;
      if (to) crmParams.dateTo = to;
      try {
        const c = await crmGetDashboard(crmParams);
        setCrm(c.data);
        setCrmError('');
      } catch (e) {
        setCrm(null);
        setCrmError(e.response?.status === 403
          ? 'تصنيف التواصل متاح للمدير فقط.'
          : 'تعذر جلب بيانات التصنيف.');
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [activeFilter, customFrom, customTo, queue]);

  useEffect(() => { load(); }, [load]);

  const pickQuick = (key) => { setCustomFrom(''); setCustomTo(''); setActiveFilter(key); };

  const ToolBar = ({ onPrint, onCsv }) => (
    <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
      <button className="btn btn-outline btn-sm" onClick={onPrint}><FiPrinter /> طباعة</button>
      <button className="btn btn-outline btn-sm" onClick={onCsv}><FiDownload /> Excel</button>
    </div>
  );

  const StatBox = ({ label, value, color }) => (
    <div style={{ background: '#fff', padding: 20, borderRadius: 8, borderRight: `4px solid ${color}` }}>
      <div style={{ fontSize: 13, color: '#666', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700 }}>{value}</div>
    </div>
  );

  const renderOverall = () => {
    const o = data?.overall || {};
    return (
      <div id="report-overall">
        <ToolBar
          onPrint={() => printSection('report-overall')}
          onCsv={() => exportCSV([o], 'overall_report.csv')}
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          <StatBox label="إجمالي المكالمات" value={o.totalCalls || 0} color="#3b82f6" />
          <StatBox label="مردود عليها" value={o.answeredCalls || 0} color="#10b981" />
          <StatBox label="فائتة" value={o.missedCalls || 0} color="#ef4444" />
          <StatBox label="نسبة الرد" value={`${o.responseRate || 0}%`} color="#8b5cf6" />
          <StatBox label="تمت متابعتها" value={o.followedUp || 0} color="#10b981" />
          {/* pendingFollowUp merges pending + in_progress + retry_later, so it is
              split here the same way the dashboard splits it — otherwise the
              in-progress numbers would show in both boxes. Merged value kept as
              the fallback for older API responses. */}
          <StatBox label="بانتظار المتابعة" value={o.pendingOnlyFollowUp ?? o.pendingFollowUp ?? 0} color="#f59e0b" />
          <StatBox label="جاري المتابعة" value={o.inProgressFollowUp || 0} color="#3b82f6" />
          <StatBox label="نسبة المتابعة" value={`${o.followUpRate || 0}%`} color="#8b5cf6" />
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Batch 3 — classification breakdown.
  //
  // Answers "of the calls the system captured, how many were real business?".
  // Archived rows are shown as their own buckets and never folded into
  // lost/contracted — see the backend split. The buckets come from
  // `archived.byType`, so retired classifications appear only while historical
  // rows still carry them.
  // ---------------------------------------------------------------------------
  const renderClassification = () => {
    if (crmError) return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>{crmError}</div>;
    const c = crm && crm.classification;
    if (!c) return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>لا توجد بيانات تصنيف</div>;

    const pending = c.pending || { count: 0, byAgent: [] };
    const archived = c.archived || { count: 0, byType: [] };
    const real = c.real || { count: 0 };
    const classified = real.count + archived.count;
    const rate = classified ? Math.round((real.count / classified) * 100) : 0;

    const csvRows = [
      { التصنيف: 'فرصة حقيقية', العدد: real.count },
      ...archived.byType.map(b => ({ التصنيف: b.label, العدد: b.count })),
      { التصنيف: 'بانتظار التصنيف', العدد: pending.count }
    ];

    return (
      <div id="report-classification" className="crm-scope">
        <ToolBar
          onPrint={() => printSection('report-classification')}
          onCsv={() => exportCSV(csvRows, 'classification_report.csv')}
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
          <StatBox label="فرصة حقيقية" value={real.count} color="#10b981" />
          {archived.byType.map(b => (
            <StatBox key={b.key} label={b.label} value={b.count} color="#f59e0b" />
          ))}
          <StatBox label="بانتظار التصنيف" value={pending.count} color="#6366f1" />
          <StatBox label="نسبة الفرص الحقيقية" value={`${rate}%`} color="#8b5cf6" />
        </div>

        <div style={{ background: '#fff', borderRadius: 8, padding: 16, marginBottom: 16, fontSize: 13, color: '#555', lineHeight: 1.9 }}>
          من <b>{classified}</b> مكالمة مصنَّفة، <b>{real.count}</b> كانت فرصاً حقيقية دخلت مسار المتابعة.
          المكالمات المؤرشفة لا تُحتسب ضمن الفرص ولا ضمن الخسائر ولا تؤثر على نسبة التحويل.
          {pending.count > 0 && <> ولا تزال <b>{pending.count}</b> مكالمة بحاجة إلى تصنيف.</>}
        </div>

        {pending.byAgent && pending.byAgent.length > 0 && (
          <div style={{ background: '#fff', borderRadius: 8, overflow: 'hidden' }}>
            <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: '#f9fafb' }}>
                <tr>
                  <th style={{ padding: 12, textAlign: 'right' }}>الموظف</th>
                  <th style={{ padding: 12, textAlign: 'right' }}>مكالمات بانتظار التصنيف</th>
                </tr>
              </thead>
              <tbody>
                {pending.byAgent.map(r => (
                  <tr key={r.extension}>
                    <td style={{ padding: 12 }}>{r.extension === '—' ? 'غير مسند' : r.extension}</td>
                    <td style={{ padding: 12 }}>{r.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderQueues = () => (
    <div id="report-queues">
      <ToolBar
        onPrint={() => printSection('report-queues')}
        onCsv={() => exportCSV(data?.byQueue || [], 'queues_report.csv')}
      />
      <div style={{ background: '#fff', borderRadius: 8, overflow: 'hidden' }}>
        <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f9fafb' }}>
            <tr>
              <th style={{ padding: 12, textAlign: 'right' }}>#</th>
              <th style={{ padding: 12, textAlign: 'right' }}>الكيو</th>
              <th style={{ padding: 12, textAlign: 'right' }}>الإجمالي</th>
              <th style={{ padding: 12, textAlign: 'right' }}>مردودة</th>
              <th style={{ padding: 12, textAlign: 'right' }}>فائتة</th>
              <th style={{ padding: 12, textAlign: 'right' }}>نسبة الرد</th>
              <th style={{ padding: 12, textAlign: 'right' }}>متابعات تمت</th>
              <th style={{ padding: 12, textAlign: 'right' }}>نسبة المتابعة</th>
            </tr>
          </thead>
          <tbody>
            {(data?.byQueue || []).map((q, i) => (
              <tr key={q.queueName} style={{ borderTop: '1px solid #eee' }}>
                <td style={{ padding: 10, fontWeight: 700 }}>{i + 1}</td>
                <td style={{ padding: 10 }}>{q.queueName}</td>
                <td style={{ padding: 10 }}>{q.total}</td>
                <td style={{ padding: 10, color: '#10b981' }}>{q.answered}</td>
                <td style={{ padding: 10, color: '#ef4444' }}>{q.missed}</td>
                <td style={{ padding: 10 }}>
                  <div style={{ background: '#eee', borderRadius: 4, height: 18, position: 'relative', minWidth: 100 }}>
                    <div style={{ background: q.responseRate >= 70 ? '#10b981' : q.responseRate >= 40 ? '#f59e0b' : '#ef4444', height: '100%', borderRadius: 4, width: `${q.responseRate}%` }} />
                    <span style={{ position: 'absolute', top: 0, right: 6, fontSize: 12, lineHeight: '18px', fontWeight: 700 }}>{q.responseRate}%</span>
                  </div>
                </td>
                <td style={{ padding: 10 }}>{q.followedUp}</td>
                <td style={{ padding: 10, fontWeight: 700, color: q.followUpRate >= 70 ? '#10b981' : q.followUpRate >= 40 ? '#f59e0b' : '#ef4444' }}>{q.followUpRate}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderAgents = () => (
    <div id="report-agents">
      <ToolBar
        onPrint={() => printSection('report-agents')}
        onCsv={() => exportCSV(data?.byAgent || [], 'agents_report.csv')}
      />
      <div style={{ background: '#fff', borderRadius: 8, overflow: 'hidden' }}>
        <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f9fafb' }}>
            <tr>
              <th style={{ padding: 12, textAlign: 'right' }}>#</th>
              <th style={{ padding: 12, textAlign: 'right' }}>الموظف</th>
              <th style={{ padding: 12, textAlign: 'right' }}>التحويلة</th>
              <th style={{ padding: 12, textAlign: 'right' }}>مكالمات رد عليها</th>
              <th style={{ padding: 12, textAlign: 'right' }}>عملاء فريدين</th>
              <th style={{ padding: 12, textAlign: 'right' }}>فائتة لم يرد عليها</th>
              <th style={{ padding: 12, textAlign: 'right' }}>متابعات أتمها</th>
              <th style={{ padding: 12, textAlign: 'right' }}>الإجمالي</th>
            </tr>
          </thead>
          <tbody>
            {(data?.byAgent || []).map((a, i) => (
              <tr key={a.extension} style={{ borderTop: '1px solid #eee' }}>
                <td style={{ padding: 10, fontWeight: 700 }}>{i + 1}</td>
                <td style={{ padding: 10 }}>{a.fullName}</td>
                <td style={{ padding: 10 }}>{a.extension}</td>
                <td style={{ padding: 10, color: '#10b981', fontWeight: 600 }}>{a.answered}</td>
                <td style={{ padding: 10, color: '#0d9488', fontWeight: 600 }}>{a.uniqueClients || 0}</td>
                <td style={{ padding: 10, color: '#ef4444', fontWeight: 600 }}>{a.missedInQueue || 0}</td>
                <td style={{ padding: 10, color: '#3b82f6', fontWeight: 600 }}>{a.followUps}</td>
                <td style={{ padding: 10, fontWeight: 700 }}>{a.answered + a.followUps}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderFollowUps = () => {
    const f = data?.followUps || {};
    return (
      <div id="report-followups">
        <ToolBar
          onPrint={() => printSection('report-followups')}
          onCsv={() => exportCSV([f], 'followups_report.csv')}
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          <StatBox label="إجمالي الفائتة" value={f.missed || 0} color="#ef4444" />
          <StatBox label="تمت متابعتها" value={f.followedUp || 0} color="#10b981" />
          <StatBox label="لم تتم متابعتها" value={f.notFollowedUp || 0} color="#f59e0b" />
          {/* Drill-down, NOT a fifth disjoint bucket: the in-progress numbers are
              a subset of "لم تتم متابعتها" above. The four boxes around it still
              decompose as followedUp + notFollowedUp = missed. */}
          <StatBox label="جاري المتابعة" value={f.inProgressFollowUp || 0} color="#3b82f6" />
          <StatBox label="نسبة الإنجاز" value={`${f.completionRate || 0}%`} color="#8b5cf6" />
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: 24, fontFamily: 'Tajawal, Arial, sans-serif' }}>
      <h2 style={{ marginTop: 0, marginBottom: 20 }}>التقارير</h2>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {QUICK_FILTERS.map(f => (
          <button
            key={f.key}
            className={`btn btn-sm ${activeFilter === f.key && !customFrom && !customTo ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => pickQuick(f.key)}
          >
            {f.label}
          </button>
        ))}
        <span style={{ marginRight: 12, color: '#666', fontSize: 13 }}>أو فترة مخصصة:</span>
        <span style={{ fontSize: 13, color: '#666' }}>من</span>
        <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="filter-input" style={{ width: 150 }} />
        <span style={{ fontSize: 13, color: '#666' }}>إلى</span>
        <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="filter-input" style={{ width: 150 }} />
        <MultiSelectQueues queues={queues} selected={queue} onChange={setQueue} />
        {(customFrom || customTo) && (
          <button className="btn btn-outline btn-sm" onClick={() => { setCustomFrom(''); setCustomTo(''); }}>مسح</button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid #eee', marginBottom: 20 }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '10px 20px',
              border: 'none',
              background: 'transparent',
              borderBottom: tab === t.key ? '3px solid #3b82f6' : '3px solid transparent',
              fontWeight: tab === t.key ? 700 : 400,
              color: tab === t.key ? '#3b82f6' : '#666',
              cursor: 'pointer',
              fontSize: 15,
              fontFamily: 'inherit'
            }}
          >
            <FiFileText style={{ marginLeft: 6 }} /> {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}>جاري التحميل...</div>
      ) : !data ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>لا توجد بيانات</div>
      ) : (
        <>
          {tab === 'overall' && renderOverall()}
          {tab === 'queues' && renderQueues()}
          {tab === 'agents' && renderAgents()}
          {tab === 'followups' && renderFollowUps()}
          {tab === 'classification' && renderClassification()}
        </>
      )}
    </div>
  );
};

export default ReportsPage;
